import { TURNOVER_MODULES } from '@ip/shared-types';
import type { PermissionModule, UserPermissions, UserRoleName } from '@ip/shared-types';

/**
 * True when the acting user holds the DB 'admin' role in at least one
 * company — mirrors the backend's getAdministeredNips/assertCanManage
 * (apps/backend/src/modules/users/users-authz.util.ts). Superadmin is
 * checked separately by callers (permissions.isSuperAdmin / isSuperAdmin).
 */
export function hasCompanyAdminRole(permissions: UserPermissions | null): boolean {
  if (!permissions) return false;
  return Object.values(permissions.companies).some((access) => access.roles.includes('admin'));
}

/**
 * True when the user holds any of `roles` in company `nip` (or is
 * superadmin). Mirrors the backend's checkCompanyRole gate used for
 * financial-record mutations (payment delete, note edit/delete) — UI-side
 * this only HIDES the buttons; the backend remains the source of truth.
 * Unlike hasModuleAccess there is NO permissive fallback while permissions
 * are loading: destructive controls stay hidden until we KNOW the user's
 * role (a flash of a delete button that would 403 is worse than a delayed
 * one). Callers should OR this with useAuth().isSuperAdmin so legacy
 * Entra-group admins (permissions === null) keep their access.
 */
export function hasCompanyRole(
  permissions: UserPermissions | null,
  nip: string,
  roles: UserRoleName[],
): boolean {
  if (!permissions) return false;
  if (permissions.isSuperAdmin) return true;
  const access = permissions.companies[nip];
  if (!access) return false;
  return access.roles.some((role) => roles.includes(role));
}

/**
 * Whether the user may see `module` for company `nip`. Superadmins and
 * legacy (Entra-group-mapped) users carry modules: 'all' and see everything,
 * matching current behavior exactly. Falls back to `true` when permissions
 * haven't loaded yet (or the company isn't present) so pre-B6 sessions don't
 * lose access mid-migration — the backend is still the source of truth.
 */
export function hasModuleAccess(
  permissions: UserPermissions | null,
  nip: string,
  module: PermissionModule,
): boolean {
  if (!permissions) return true;
  if (permissions.isSuperAdmin) return true;
  const access = permissions.companies[nip];
  if (!access) return true;
  return access.modules === 'all' || access.modules.includes(module);
}

/**
 * Whether the user may see contractor turnover and invoice counters for
 * company `nip` (operator decision 2026-09-10). Money is invoice data, so it
 * takes the 'expense' or 'sales' module (TURNOVER_MODULES) — the
 * 'contractors' module alone (what the 'crm' role now carries) opens the
 * counterparty directory, never the amounts. Mirrors the backend, which
 * nulls those fields of ContractorListItem and 403s the contractor
 * dashboard; like hasModuleAccess it stays permissive while permissions
 * haven't loaded.
 */
export function canSeeTurnover(permissions: UserPermissions | null, nip: string): boolean {
  return TURNOVER_MODULES.some((module) => hasModuleAccess(permissions, nip, module));
}

/**
 * Whether the user may download a customer statement PDF for company `nip`.
 * Mirrors the backend StatementService gate: module 'sales' and NOT
 * tag-restricted in 'sales' (a statement is a per-customer total and would
 * leak amounts past the tag filter → 403). Unlike hasModuleAccess there is no
 * permissive fallback: the button stays hidden until permissions are known.
 * Callers should OR this with useAuth().isSuperAdmin (as with hasCompanyRole),
 * which also covers legacy Entra-group admins (permissions === null).
 */
export function canExportSalesStatement(
  permissions: UserPermissions | null | undefined,
  nip: string,
): boolean {
  if (!permissions) return false;
  const access = permissions.companies[nip];
  if (!access) return false;
  if (access.modules !== 'all' && !access.modules.includes('sales')) return false;
  return access.tagFilters?.sales === undefined;
}

/**
 * Route section a user can land on after picking a company, in preference
 * order. Used by RootRedirect to avoid sending a module-restricted user
 * (e.g. a `pracownik` with only the 'hr' module) to a section they can't
 * see. `otherCompanyOk: false` sections don't apply to the 'other'
 * (Unassigned) pseudo-company — mirrors AppSidebar's NOT_APPLICABLE_TO_OTHER.
 */
const ROOT_REDIRECT_SECTIONS: ReadonlyArray<{
  path: string;
  module: PermissionModule;
  otherCompanyOk: boolean;
}> = [
  { path: 'dashboard', module: 'dashboard', otherCompanyOk: true },
  { path: 'invoices', module: 'expense', otherCompanyOk: true },
  { path: 'sales', module: 'sales', otherCompanyOk: false },
  { path: 'crm', module: 'crm', otherCompanyOk: false },
  { path: 'fiscal', module: 'fiscal', otherCompanyOk: false },
  { path: 'projects', module: 'projects', otherCompanyOk: false },
  { path: 'contractors', module: 'contractors', otherCompanyOk: true },
  { path: 'magazyn', module: 'magazyn', otherCompanyOk: false },
  { path: 'employees', module: 'employees', otherCompanyOk: false },
  { path: 'mileage', module: 'mileage', otherCompanyOk: false },
  { path: 'tags', module: 'tags', otherCompanyOk: true },
  { path: 'hr', module: 'hr', otherCompanyOk: false },
];

/**
 * First route path (relative to `/:nip/`) a module-restricted user may see
 * for company `nip`, in ROOT_REDIRECT_SECTIONS preference order. Returns
 * null when the user's access for this company is module-restricted but
 * grants none of the known sections (e.g. an empty/misconfigured module
 * list) — callers should fall back to a safe default in that case.
 *
 * Callers must only invoke this for module-restricted access (a company
 * whose `modules` is an array, not `'all'`) — superadmins and 'all'-module
 * users should keep going straight to /invoices, matching pre-existing
 * behavior; this helper does not special-case that so it stays a pure
 * "first permitted section" lookup.
 */
export function firstAccessibleSection(
  permissions: UserPermissions | null,
  nip: string,
): string | null {
  // Tag-restricted access (expense/sales tagFilters) is hard-403'd by the
  // backend on the aggregate dashboard/financial-overview endpoints — never
  // land such a user on a guaranteed-broken dashboard; their tag-filtered
  // invoice/sales lists work fine.
  const tagFilters = permissions?.companies[nip]?.tagFilters;
  const tagRestricted = !!(tagFilters && (tagFilters.expense || tagFilters.sales));
  const section = ROOT_REDIRECT_SECTIONS.find((candidate) => {
    if (nip === 'other' && !candidate.otherCompanyOk) return false;
    if (candidate.path === 'dashboard' && tagRestricted) return false;
    return hasModuleAccess(permissions, nip, candidate.module);
  });
  return section?.path ?? null;
}

/**
 * Landing section (route path relative to `/:nip/`) for company `nip` — THE
 * single decision point for every hard-coded destination: RootRedirect
 * (post-login), the sidebar company switcher, and the /:nip index redirect.
 *
 * Superadmins and legacy / unrestricted ('all'-module) users keep going to
 * /invoices — that's what they see today and changing it would surprise the
 * operator. Module-restricted users (e.g. a `pracownik` with only 'hr') get
 * routed to the first section their permissions actually grant, so they
 * never land on a page the backend will reject.
 */
export function landingSectionFor(
  isSuperAdmin: boolean,
  permissions: UserPermissions | null,
  nip: string,
): string {
  if (isSuperAdmin || !permissions) return 'invoices';
  const access = permissions.companies[nip];
  if (!access || access.modules === 'all') return 'invoices';
  return firstAccessibleSection(permissions, nip) ?? 'invoices';
}
